import React, { useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziGrid, { GeneralActions, RowActionsExtra } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEdit, FiEye, FiTrash2 } from "react-icons/fi";
import { HiOutlinePlusCircle } from "react-icons/hi";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { PurchaseOrder } from "../../../models/purchase.order";
import { Searches } from "../../../models/shipeezi.table";
import { ViewOnly } from "../../../types";
import { OrderStatus } from "../../../util/enums";
import { paginate } from "../../../util/paginate";
import { usePurchaseOrderColumns } from "../../Booking/hooks/usePurchaseOrderColumns";
import Button, { Color } from "../../Form/Button";
import { GridQuantity } from "../../GridQuantity";
import { DefaultTooltip } from "../Tooltip";
import AddNewOrderModal, { AddNewOrderHandles } from "./AddOrder";
import { ShipmentOrderEmptySection } from "./components/EmptySection";
import RemovePurchaseOrderModal, {
  RemovePurchaseOrderConfirmationModalHandles,
} from "./ModalRemovePurchaseOrder";
import useStyles from "./styles";
import { filterPurchaseOrders, PurchaserOrderFrom, SearchAttribute } from "./types";

interface ShipmentOrdersProps {
  purchaseOrders: PurchaseOrder[];
  onAddPurchaseOrder?: (orders: PurchaseOrder[]) => void;
  onRemovePurchaseOrder?: (order: PurchaseOrder) => void;
  viewOnly: ViewOnly;
  loadingOrders: boolean;
  orderList: PurchaseOrder[];
  loadingOrderList: boolean;
  refetchOrderList: () => void;
  addButton?: {
    tooltip: JSX.Element;
    disableButton: boolean;
  };
  calledFrom: PurchaserOrderFrom;
  fromMaintenance?: boolean;
  showOrderStatus?: boolean;
}

const ShipmentOrders: React.FC<ShipmentOrdersProps> = (props) => {
  const {
    purchaseOrders,
    onAddPurchaseOrder,
    onRemovePurchaseOrder,
    viewOnly: viewMode,
    loadingOrders,
    orderList,
    refetchOrderList,
    addButton,
    loadingOrderList,
    calledFrom,
    fromMaintenance,
    showOrderStatus = false,
  } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { isSupplierPortal } = useAppState();
  const { fields: viewOnly } = viewMode;
  const calledFromBooking = useMemo(() => calledFrom === PurchaserOrderFrom.BOOKING, [calledFrom]);
  const { columns, baseColumns } = usePurchaseOrderColumns(showOrderStatus && !calledFromBooking);

  const newOrderRef = useRef<AddNewOrderHandles>(null);
  const removePurchaseOrderModalRef = useRef<RemovePurchaseOrderConfirmationModalHandles>(null);
  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const purchaseOrderData = useMemo(() => {
    let data = purchaseOrders;

    if (searchAttributes.length > 0) {
      data = filterPurchaseOrders(purchaseOrders, searchAttributes);
    }

    return paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [purchaseOrders, page, pageSize, searchAttributes]);

  useEffect(() => {
    refetchOrderList();
  }, []);

  const renderOrdersQuantity = () => (
    <GridQuantity
      title={translate("orders")}
      value={purchaseOrders.length}
      valueProps={{ "data-cy": "quantity" }}
    />
  );

  const onAddNewOrder = () => {
    newOrderRef.current?.open(purchaseOrders.map((purchaseOrder) => Number(purchaseOrder.id)));
  };

  const handleOpenModal = (orderToRemoveId: number | string) => {
    const orderFound = orderList.find((order) => Number(order.id) === Number(orderToRemoveId));

    if (orderFound) removePurchaseOrderModalRef.current?.open(orderFound);
  };

  const handleRemoveOrder = (orderToRemoveId: number | string) => {
    const orderFound = orderList.find((order) => Number(order.id) === Number(orderToRemoveId));

    if (orderFound) onRemovePurchaseOrder?.(orderFound);
    removePurchaseOrderModalRef.current?.close();
  };

  const handleRemoveButton = (orderToRemoveId: number | string) => {
    if ((!isSupplierPortal && calledFromBooking) || viewOnly) return null;

    return (
      <>
        <Tooltip data-cy="action-remove" title={translate("remove")} placement="top">
          <div>
            <FiTrash2
              color="#F97066"
              size={20}
              className={classes.button}
              onClick={() => {
                handleOpenModal(orderToRemoveId);
              }}
            />
          </div>
        </Tooltip>
        <RemovePurchaseOrderModal
          onSubmit={(orderId: number) => handleRemoveOrder(orderId)}
          ref={removePurchaseOrderModalRef}
          from={calledFromBooking ? translate("fromThisBooking") : translate("fromThisShipment")}
          title={translate("removePurchaseOrder")}
          warningText={translate("allSSCCsInsideThePurchaseOrderWillAlsoBeRemoved")}
        />
      </>
    );
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const onAddOrdersSelected = (ordersSelecteds: PurchaseOrder[]) => {
    onAddPurchaseOrder?.(ordersSelecteds);
  };

  const addNewOrder = () => {
    if (viewOnly) return null;

    return (
      <DefaultTooltip title={addButton?.disableButton ? addButton.tooltip : translate("order")}>
        <div>
          <Button
            customColor={Color.SECONDARY}
            onClick={() => onAddNewOrder()}
            disabled={addButton?.disableButton}
          >
            <div className={classes.addOrder}>
              <HiOutlinePlusCircle size={18} color="#FFF" />
              <span>{translate("order")}</span>
            </div>
          </Button>
        </div>
      </DefaultTooltip>
    );
  };

  const orderRight = () => {
    if (!viewOnly) return ["handleNewOrder"];
    if (!fromMaintenance) return ["renderOrdersQuantity"];

    return undefined;
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left:
        !viewOnly || fromMaintenance
          ? ["handleSearch", "handleFilterBy", "renderOrdersQuantity"]
          : undefined,
      right: orderRight(),
    },
    renderOrdersQuantity: renderOrdersQuantity(),
    handleSearch: {
      onSearch: handleSearch,
    },
    handleNewOrder: addNewOrder(),
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      buttonAndAction: (rowId, row: PurchaseOrder) => {
        const isCancelled = row && row.status === OrderStatus.CANCELLED;
        const tooltipMessage = isCancelled ? translate("thisOrderIsCancelled") : translate("edit");

        if ((row && row.status === OrderStatus.CONFIRMED) || isSupplierPortal) return null;

        return (
          <DefaultTooltip title={tooltipMessage}>
            <div
              onClick={() =>
                !isCancelled && !viewOnly && history.push(`${PRIVATE_ROUTES.ORDERS_MNT}/${rowId}`)
              }
              className={classes[isCancelled || viewOnly ? "buttonOff" : "button"]}
            >
              <FiEdit color="#475467" size={18} />
            </div>
          </DefaultTooltip>
        );
      },
    },
    {
      title: translate("details"),
      buttonAndAction: (rowId) => {
        return (
          <DefaultTooltip title={translate("details")}>
            <div
              onClick={() =>
                history.push({
                  pathname: `${PRIVATE_ROUTES.ORDERS_MNT}/${rowId}`,
                  state: {
                    viewOnly: true,
                  },
                })
              }
              className={classes.button}
            >
              <FiEye color="#475467" size={18} />
            </div>
          </DefaultTooltip>
        );
      },
    },
    { title: translate("remove"), key: "remove", buttonAndAction: handleRemoveButton },
  ];

  return (
    <div className={classes.grid}>
      <AddNewOrderModal
        ref={newOrderRef}
        gridColumns={baseColumns}
        onAddOrders={onAddOrdersSelected}
        orderList={orderList}
        loading={loadingOrders || loadingOrderList}
        showOrderStatus={!calledFromBooking}
      />
      <ShipeeziGrid
        data={purchaseOrderData}
        totalItems={purchaseOrders.length}
        columns={columns}
        loadingData={loadingOrders}
        emptyData={() => <ShipmentOrderEmptySection />}
        currentPage={page}
        onNextPage={setPage}
        onPreviousPage={setPage}
        onGoToPage={setPage}
        pageSize={pageSize}
        onChangePageSize={setPageSize}
        pageSizeList={[20, 40, 60, 100]}
        generalActions={generalActions}
        positionActionsColumn="right"
        rowActionsExtra={rowActionsExtra}
        showColumnChooser={false}
        showSelection={!viewOnly}
      />
    </div>
  );
};

export default ShipmentOrders;
